import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import './TimelineAdmin.css';

const TimelineAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleTimelineUpdate = (index, field, value) => {
    updateContent(`${contentPath}.timeline.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleDetailUpdate = (timelineIndex, detailIndex, value) => {
    updateContent(`${contentPath}.timeline.${timelineIndex}.details.${detailIndex}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddDetail = (timelineIndex) => {
    const timelineItem = sectionData.timeline[timelineIndex];
    const details = timelineItem.details || [];
    updateContent(`${contentPath}.timeline.${timelineIndex}.details`, [...details, '']);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteDetail = (timelineIndex, detailIndex) => {
    const timelineItem = sectionData.timeline[timelineIndex];
    const details = timelineItem.details.filter((_, i) => i !== detailIndex);
    updateContent(`${contentPath}.timeline.${timelineIndex}.details`, details);
    setSectionData(getContent(contentPath));
  };

  const handleAddTimelineItem = () => {
    const timeline = sectionData?.timeline || [];
    const newItem = {
      year: '',
      title: '',
      summary: '',
      details: []
    };

    updateContent(`${contentPath}.timeline`, [...timeline, newItem]);
    setSectionData(getContent(contentPath));
  };

  const handleDeleteTimelineItem = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément de chronologie ?')) {
      const timeline = sectionData?.timeline || [];
      const updatedTimeline = timeline.filter((_, i) => i !== index);
      updateContent(`${contentPath}.timeline`, updatedTimeline);
      setSectionData(getContent(contentPath));
    }
  };

  if (!sectionData) {
    return (
      <div className="timeline-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="timeline-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-history"></i>
            Chronologie
          </h2>
          <p>Gérez la chronologie historique de votre organisation</p>
        </div>
        <button 
          className="btn-add-timeline"
          onClick={handleAddTimelineItem}
        >
          <i className="fas fa-plus"></i>
          Ajouter un événement
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder="Notre Historique"
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Description de votre chronologie..."
            rows={2}
          />
        </div>
        <div className="setting-group">
          <label>Introduction (pour le bloc historique résumé)</label>
          <textarea
            value={sectionData.intro || ''}
            onChange={(e) => handleSectionUpdate('intro', e.target.value)}
            placeholder="De nos débuts modestes à notre position actuelle..."
            rows={2}
          />
        </div>
        <div className="setting-group">
          <label>Image (pour le bloc historique résumé)</label>
          <ImageUpload
            value={sectionData.image}
            onChange={(url) => handleSectionUpdate('image', url)}
            label="Télécharger une image"
          />
        </div>
      </div>

      {/* Timeline List */}
      <div className="timeline-list-admin">
        {(sectionData.timeline || []).map((item, index) => (
          <div key={index} className="timeline-item-admin">
            <div className="timeline-header">
              <div className="timeline-title-area">
                <h3>
                  <i className="fas fa-calendar-alt"></i>
                  Événement {index + 1}
                </h3>
                {item.year && (
                  <span className="timeline-year">
                    {item.year}
                  </span>
                )}
              </div>
              <div className="timeline-actions">
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTimelineItem(index)}
                  title="Supprimer cet événement"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Clean Horizontal Form Fields */}
            <div className="form-grid">
              <div className="form-group">
                <label>Année</label>
                <input
                  type="text"
                  value={item.year || ''}
                  onChange={(e) => handleTimelineUpdate(index, 'year', e.target.value)}
                  placeholder="2010"
                />
              </div>

              <div className="form-group">
                <label>Titre</label>
                <input
                  type="text"
                  value={item.title || ''}
                  onChange={(e) => handleTimelineUpdate(index, 'title', e.target.value)}
                  placeholder="Fondation de DentoXcellence"
                />
              </div>

              <div className="form-group full-width">
                <label>Résumé</label>
                <textarea
                  value={item.summary || ''}
                  onChange={(e) => handleTimelineUpdate(index, 'summary', e.target.value)}
                  placeholder="Description courte de l'événement..."
                  rows={3}
                />
              </div>

              {/* Details Section */}
              <div className="form-group full-width">
                <div className="details-section">
                  <div className="details-header">
                    <label>Détails</label>
                    <button
                      type="button"
                      className="btn-add-detail"
                      onClick={() => handleAddDetail(index)}
                    >
                      <i className="fas fa-plus"></i>
                      Ajouter
                    </button>
                  </div>
                  
                  <div className="details-list">
                    {(item.details || []).map((detail, detailIndex) => (
                      <div key={detailIndex} className="detail-item">
                        <input
                          type="text"
                          value={detail}
                          onChange={(e) => handleDetailUpdate(index, detailIndex, e.target.value)}
                          placeholder="Détail de l'événement..."
                        />
                        <button
                          type="button"
                          className="btn-delete-detail"
                          onClick={() => handleDeleteDetail(index, detailIndex)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {(!sectionData.timeline || sectionData.timeline.length === 0) && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-history"></i>
          </div>
          <h3>Aucun événement chronologique</h3>
          <p>Commencez par ajouter votre premier événement historique.</p>
          <button className="btn-add-first" onClick={handleAddTimelineItem}>
            <i className="fas fa-plus"></i>
            Ajouter le premier événement
          </button>
        </div>
      )}
    </div>
  );
};

export default TimelineAdmin;



